import './context-menu.less'
import classnames from 'classnames';
import { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';
import React from 'react';
import ReactDOM from 'react-dom';

const MOUSE = {
    LEFT: 0,
    MIDDLE: 1,
    RIGHT: 2,
}

interface IProps {
    left?: number; // 菜单左边距
    top?: number; // 菜单上边距

    autoAdjust?: boolean    // 自动判断位置使菜单不超出屏幕区域

    width?: number; // 菜单宽度
    visible?: boolean; // 菜单是否可见
    mask?: boolean; // 是否显示遮罩层
    wrapperClassname?: string; // 自定义菜单容器的样式类名
    children?: ReactNode; // 菜单内容
    forceRender?: boolean; // 是否强制渲染菜单
    onCancel?: () => void; // 点击遮罩层或外部区域时的回调
    onEscape?: () => void; // 按下 Escape 键的回调
    onRightClick?: (e: React.MouseEvent) => void
    maskStyle?: CSSProperties; // 自定义遮罩层样式
    modalRender?: (modal: ReactNode) => ReactNode; // 自定义菜单内容的高阶函数,可以在modal体外包裹组件，实现自定义功能，如拖拽
    onMouseLeave?: () => void;// 鼠标离开菜单区域时的回调
}
function ContextMenu(props: IProps) {
    // 定义状态
    let [container, setContainer] = useState<HTMLDivElement>(null); // 用于保存菜单的 DOM 容器
    let [bottom, setBottom] = useState<number>(); // 菜单的底部位置（用于自动调整）
    let [right, setRight] = useState<number>(); // 菜单的右侧位置（用于自动调整）
    const ref = useRef<HTMLDivElement>(); // 保存菜单的 DOM 引用
    const [menuWidth, setMenuWidth] = useState<number | null>(null); // 保存菜单实际宽度
    const [isHovering, setIsHovering] = useState(false); // 跟踪鼠标是否在菜单上

    useEffect(() => {
        document.addEventListener('keydown', onKeyup); // 注册键盘事件
        if (props.forceRender) {
            let container = document.createElement('div'); // 创建一个容器 DOM 节点
            setContainer(container);
            document.body.appendChild(container); // 将容器添加到 body
        }
        return () => {
            container?.parentElement?.removeChild(container); // 销毁时清理 DOM 节点
            document.removeEventListener('keydown', onKeyup); // 移除键盘事件
        };
    }, []);

    const onKeyup = (event) => {
        let keyCode = event.keyCode; // 获取按键码
        if (keyCode === 27) {
            // Escape 键
            if (props.onEscape) {
                props.onEscape(); // 调用 Escape 的回调
            }
        }
    }

    useEffect(() => {
        if (!!props.visible) {
            // 如果菜单可见且容器不存在
            if (container == null) {
                container = document.createElement('div');
                setContainer(container);
            }
            document.body.appendChild(container);
        }
        if (!props.visible && !props.forceRender) {
            // 菜单不可见且未强制渲染时
            container?.parentElement.removeChild(container);
            setContainer(null);
        }
    }, [props.visible]);

    useEffect(() => {
        if (!ref.current) return;

        // 获取容器的实际宽度（如果没有传递 width）
        if (!props.width) {
            setMenuWidth(ref.current?.clientWidth || null); // 获取子元素撑开的宽度
        }

        let clientH = document.body.clientHeight; // 获取屏幕高度
        if (props.autoAdjust && ref.current.clientHeight + props.top > clientH) {
            // 自动调整菜单高度
            setBottom(clientH - props.top);
        } else {
            setBottom(null);
        }

        let clientW = document.body.clientWidth; // 获取屏幕宽度
        if (props.autoAdjust && props.left + ref.current.clientWidth > clientW) {
            // 自动调整菜单宽度
            setRight(0);
        } else {
            setRight(null);
        }
    }, [props.visible, container, props.width, menuWidth]);

    const onClick = (e) => {
        if (e.button === MOUSE.LEFT) {
            if (checkClickInBound(e)) return;
            if (props.onCancel) {
                props.onCancel();
            }
        }

    }

    const onRightClick = (e) => {
        if (props.onRightClick) {
            e.preventDefault();
            props.onRightClick(e);
        }
        else if (props.onCancel) {
            e.preventDefault();
            props.onCancel();
        }
    }

    const checkClickInBound = (e) => {
        let bound = ref.current?.getBoundingClientRect();
        if (!bound) return false;
        let x = e.clientX;
        let y = e.clientY;
        return x > bound.left && x < bound.right && y > bound.top && y < bound.bottom;
    }

    // 处理鼠标进入菜单区域
    const handleMouseEnter = () => {
        setIsHovering(true);
    }

    // 处理鼠标离开菜单区域
    const handleMouseLeave = () => {
        setIsHovering(false);
        if (props.onMouseLeave) {
            props.onMouseLeave();
        }
    }

    let modal = (
        <div className={classnames({
            'context-menu-container': true,
            'visible': props.visible,
            'mask': props.mask,
        })} onClick={onClick} onContextMenu={e => onRightClick(e)} style={props.maskStyle}>
            <div className={`context-menu-wrapper ${props.wrapperClassname || ''}`} ref={ref} style={{
                left: right == null ? props.left : null,
                top: bottom == null ? props.top : null,
                bottom: bottom,
                right: right,
                width: props.width || menuWidth,
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            >
                {props.children}
            </div>
        </div>
    )

    return (
        (props.visible || props.forceRender) &&
        container &&
        (ReactDOM.createPortal(
            !!props.modalRender ? props.modalRender(modal) : modal, container))
    )
}
ContextMenu.defaultProps = {
    mask: true,
    forceRender: false,
    left: 0,
    top: 0,
    autoAdjust: true,
}
export default ContextMenu;